import React from 'react';
import {useDeviceContext} from "../TelematicsDevice/TelematicsDeviceProvider";
import {useLegacyTelematicsDeviceContext} from "../LegacyTelematicsDevice/LegacyTelematicsDeviceProvider";
import {GatewayStateEnum} from "../LegacyTelematicsDevice/LegacyTelematicsDeviceApi";
import {LocationService} from "../../../../components/Location/LocationService";
import {getUserInfo} from "../../../Login/AuthenticationService";
import {useOktaAuth} from "@okta/okta-react";
import {AccessControlMode} from "../../components/DeviceAccessControl/DeviceAccessControl";
import {isFirmwareGreaterThanOrEqualTo} from "../TelematicsDevice/FirmwareComparison";
import { ConfigurationAccessType, DigitalKeyModeEnum} from "../TelematicsDevice/DeviceStateApi";
import { DeviceTypeEnum, hasCanSupport } from '../TelematicsDevice/DeviceTypeEnum';
import { useIrisPermissions } from '../../../../hooks/useIrisPermission';

export type Visibility = {
  visible: boolean;
  readonly: boolean;
}

export type VisibilityExtended = {
  access?: ConfigurationAccessType;
  accessReason?: string;
} & Visibility

export const LEGACY_NOT_SUPPORTED = "LEGACY_NOT_SUPPORTED";
export const ACCESS_CONTROL_ENABLED = "ACCESS_CONTROL_ENABLED";

export type Accessibility = {
  outputMode: VisibilityExtended;
  inputFiltering: VisibilityExtended;
  groups: VisibilityExtended;
  classicAccessControl: Visibility;
  digitalKey: VisibilityExtended;
  relayConfiguration: VisibilityExtended;
  inputNames: Visibility;
  powerMode: Visibility;
  reboot: Visibility;
  firmwareUpdate: Visibility;
  calibrateTiltSensor: Visibility;
  hiltiServiceVan: {};
  hiltiBluetoothScanning: {};
  legacyGenericConfigurations: Visibility;
  legacyCustomerCommands: Visibility;
  inputConfig: Visibility;
  canProfile1: VisibilityExtended;
  canProfile2: VisibilityExtended;
  modbusProfile: Visibility;
  replace: Visibility;
  input5Input6: Visibility;
  activityDetection: Visibility;
  can1ActivityDetection: Visibility;
  can2ActivityDetection: Visibility;
  operatingMode: VisibilityExtended;
  resetOperatingHours: Visibility;
  wifiSetup: Visibility;
  totalDistance: VisibilityExtended;
  totalOperatingHours: VisibilityExtended;
  name: Visibility;
  referenceNumber: Visibility;
  note: Visibility;
}

type AccessibilityContextValue = {
  accessibility?: Accessibility;
  setAccessibility: (accessibility: Accessibility) => void;
};

const AccessibilityContext = React.createContext<
  AccessibilityContextValue | undefined
>(undefined);

type AccessibilityProviderProps = {
  children: React.ReactNode;
};

const AccessibilityProvider = (props: AccessibilityProviderProps) => {
  const [accessibility, setAccessibility] = React.useState<Accessibility>();
  let deviceContext = useDeviceContext();
  const legacyTelematicsDevice = useLegacyTelematicsDeviceContext();
  const { authState } = useOktaAuth();
  const { isFleetAdmin } = useIrisPermissions();

  const value: AccessibilityContextValue = React.useMemo(
    () => ({
      accessibility: accessibility,
      setAccessibility: setAccessibility
    }),
    [accessibility]
  );

  const isOnIris = () : boolean => {
    return isTU700() || legacyTelematicsDevice.telematicsDevice?.gatewayState === GatewayStateEnum.Iris;
  }

  const hasInputConfigThroughTlvSupport = () : boolean => {
    return isTU700() || isFirmwareGreaterThanOrEqualTo(legacyTelematicsDevice.telematicsDevice?.firmwareVersion, 63,4);
  }

  const hasDigitalKeyThroughTlvSupport = () : boolean => {
    return isFirmwareGreaterThanOrEqualTo(legacyTelematicsDevice.telematicsDevice?.firmwareVersion, 63,6);
  }

  const hasAccessControl = () : boolean => {
    return deviceContext.state.desired.accessControl?.mode === DigitalKeyModeEnum.UNLOCKED_FOR_ALL_FIXED ||
        deviceContext.state.desired.accessControl?.mode === DigitalKeyModeEnum.UNLOCKED_FOR_ALL
  }

  const hasClassicAccessControl = () : boolean => {
    return !isOnIris()
      && legacyTelematicsDevice.telematicsDevice?.accessControlMode !== undefined
      && legacyTelematicsDevice.telematicsDevice?.accessControlMode !== AccessControlMode.doNotUseAccessKeys
  }

  const hasOperatingModeSupport = () : boolean => {
    return isTU700() || isFirmwareGreaterThanOrEqualTo(legacyTelematicsDevice.telematicsDevice?.firmwareVersion, 63, 8);
  }

  const getOperatingModeAccessReason = () : string | undefined => {
    if( !isTrackunitUser() && !isAdmin() ) {
      return "NOT_OWNER_ACCOUNT";
    }

    if( !isTU700() && !isFirmwareGreaterThanOrEqualTo(legacyTelematicsDevice.telematicsDevice?.firmwareVersion, 63, 8)) {
      return "FIRMWARE_NOT_SUPPORTED";
    }

    return undefined;
  }

  const isTU700 = () : boolean => {
    return deviceContext.identity.deviceType === DeviceTypeEnum.TU700 || deviceContext.identity.deviceType === DeviceTypeEnum.GRIFFIN;
  }

  const isRawDevice = () : boolean => {
    return deviceContext.isRawDevice(deviceContext.identity);
  }

  const isAccessManagementEnabled = () : boolean => {
    return deviceContext.stateDocumentation?.accessControl?.accessReason === "ACCESS_MANAGEMENT_ENABLED";
  }

  const isRelayConfigurationReadonly = () : boolean => {
    return deviceContext.stateDocumentation?.accessControl?.readonly === true;
  }

  const isManitouBranded = () : boolean => {
    return LocationService.isManitou();
  }

  const isTrackunitUser = () : boolean => {
    return getUserInfo(authState?.accessToken)?.customerId === 3;
  }

  const isAdmin = () : boolean => {
    return isFleetAdmin;
  }

  const isCANReadonly = () : boolean => {
    return deviceContext.stateDocumentation?.canbus?.readonly === true;
  }

  React.useEffect(() => {
    /*
      Classic Access Level: https://github.com/Trackunit/SafeTrackV5/blob/master/Documents/FromJacob/UserAccessRights.txt
    */

    if( deviceContext.identity && legacyTelematicsDevice) {
      setAccessibility({
        // The device will reject this configuration if it has an Access Control setup
        outputMode: {
          visible: true,
          readonly: !deviceContext.hasTlvSupport || hasAccessControl() || hasClassicAccessControl() || !(isAdmin() || isTrackunitUser() || isManitouBranded()),
          accessReason: !deviceContext.hasTlvSupport ? LEGACY_NOT_SUPPORTED
            : (hasAccessControl() || hasClassicAccessControl()) ? ACCESS_CONTROL_ENABLED
            : undefined
        },
        inputFiltering: {
          visible: true,
          readonly: !deviceContext.hasTlvSupport || !(isAdmin() || isTrackunitUser() || isManitouBranded()),
          accessReason: !deviceContext.hasTlvSupport ? LEGACY_NOT_SUPPORTED : undefined
        },
        // Fully migrated to Iris Groups API - no longer depends on Classic API
        groups: {
          visible: true,
          readonly: !(isAdmin() || isTrackunitUser())
        },
        // Uses Iris API (device-access-control-api) despite the "classic" name
        classicAccessControl: {
          visible: !isTU700() && !isAccessManagementEnabled(),
          // in Classic, this also requires the setting "Access to key access control"
          // Currently the device-access-control-api is used for setting this and the above check is not included. It is requiring AccessLevel 1, 2, 6 and 8
          readonly: !isAdmin()
        },
        // Server-controlled: readonly is determined by stateDocumentation.accessControl.readonly
        // Classic access control and access management also restrict editing
        digitalKey: {
          visible: isTU700() || hasDigitalKeyThroughTlvSupport(),
          readonly: hasClassicAccessControl() || deviceContext.stateDocumentation?.accessControl?.readonly || isAccessManagementEnabled() || !(isTrackunitUser() || isAdmin()),
          accessReason: hasClassicAccessControl() ? "HAS_CLASSIC_ACCESS_CONTROL" : deviceContext.stateDocumentation?.accessControl?.accessReason ? deviceContext.stateDocumentation?.accessControl?.accessReason : undefined
        },
        relayConfiguration: {
          visible: isAccessManagementEnabled() || isTrackunitUser()  ,
          readonly: !((isTrackunitUser() || !isRelayConfigurationReadonly()) && isOnIris() && (isTU700() || hasDigitalKeyThroughTlvSupport())),
          accessReason: !isTrackunitUser() && isRelayConfigurationReadonly() ? "INSUFFICIENT_PERMISSIONS" : undefined,
        },
        // Will be migrated to telemetry-interpretation
        inputNames: {
          visible: true,
          // In Classsic this also requires the "Update rights under the module 'Settings'" flag
          readonly: !(isTrackunitUser() || isAdmin())
        },
        // Uses legacy configuration from the Telematics Device API
        powerMode: {
          // There's an additional check in the Component: Only show if it has been enabled through Subscription
          visible: false,
          readonly: false
        },
        reboot: {
          visible: !isManitouBranded() && !isTU700(),
          readonly: false
        },
        firmwareUpdate: {
          visible: !isManitouBranded(),
          readonly: false
        },
        calibrateTiltSensor: {
          visible: !isManitouBranded() && !isTU700(),
          readonly: false
        },
        // Should use Device State
        inputConfig: {
          visible: !isManitouBranded() && hasInputConfigThroughTlvSupport(),
          readonly: !(isTrackunitUser() || isAdmin())
        },
        hiltiServiceVan: {
          // TODO
        },
        hiltiBluetoothScanning: {
          // TODO
        },
        legacyGenericConfigurations: {
          visible: !isManitouBranded() && !isTU700(),
          readonly: false
        },
        legacyCustomerCommands: {
          visible: !isManitouBranded() && !isTU700(),
          readonly: false
        },
        canProfile1: {
          visible: true,
          // Additional check in Component: set to readonly if list of CAN profiles cannot be fetched from Legacy
          readonly: isCANReadonly() || !hasCanSupport(deviceContext.identity?.deviceType),
          access: deviceContext.stateDocumentation?.canbus?.access,
          accessReason: deviceContext.stateDocumentation?.canbus?.accessReason
        },
        canProfile2: {
          visible: isTU700(),
          // Additional check in Component: set to readonly if list of CAN profiles cannot be fetched from Legacy
          readonly: isCANReadonly()
        },
        modbusProfile: {
          visible: isTU700(),
          readonly: !isTU700() || !(isTrackunitUser() || isAdmin())
        },
        replace: {
          visible: true,
          readonly: false
        },
        input5Input6: {
          visible: isTU700(),
          readonly: true
        },
        activityDetection: {
          visible: !isTU700(),
          readonly: true},
        can1ActivityDetection: {
          visible: isTU700(),
          readonly: true},
        can2ActivityDetection: {
          visible: isTU700(),
          readonly: true},
        operatingMode: {
          visible: isRawDevice(),
          readonly: !hasOperatingModeSupport() || !(isTrackunitUser() || isAdmin()),
          access: !hasOperatingModeSupport() || !(isTrackunitUser() || isAdmin()) ? "VIEW" : "EDIT", // TODO: Replace by rule from state-documentation when it's available
          accessReason: getOperatingModeAccessReason()
        },
        resetOperatingHours: {
          visible: isManitouBranded(),
          readonly: false
        },
        wifiSetup: {
          visible: isTU700(),
          readonly: !(isTrackunitUser() || isAdmin())
        },
        totalDistance: {
          visible: true,
          readonly: false,
        },
        totalOperatingHours: {
          visible: true,
          readonly: false,
        },
        name: {
          visible: true,
          readonly: false,
        },
        referenceNumber: {
          visible: true,
          readonly: false,
        },
        note: {
          visible: true,
          readonly: false,
        }});
    }

  }, [deviceContext.identity, deviceContext.stateDocumentation, deviceContext.hasTlvSupport, legacyTelematicsDevice.telematicsDevice, isFleetAdmin])

  // Useful for debugging
/*
  React.useEffect(() => {
    if( deviceContext.identity) {
      console.log('Accessibility', accessibility);
      console.log('Accessibility-raw', {
        hasClassicAccessControl: hasClassicAccessControl(),
        isAccessManagementEnabled: isAccessManagementEnabled(),
        isRelayConfigurationReadonly: isRelayConfigurationReadonly(),
        isTU700: isTU700(),
        isManitouBranded: isManitouBranded(),
        isTrackunitUser: isTrackunitUser(),
        hasCanSupport: hasCanSupport(deviceContext.identity?.deviceType),
        isAccessControlReadonly: deviceContext.stateDocumentation?.accessControl?.readonly
        })
    }
  }, [accessibility])
  */


  return <AccessibilityContext.Provider value={value} {...props} />;
};

const useAccessibilityContext = () => {
  const context = React.useContext(AccessibilityContext);

  if (!context) {
    throw new Error(
      'useAccessibilityContext must be used within an AccessibilityProvider'
    );
  }

  return {
    accessibility: context.accessibility
  };
};

export { AccessibilityProvider, useAccessibilityContext };
